import type { ChatMessage } from '@repo/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useAnalytics } from '../contexts/analytics/AnalyticsProvider';
import { trpc } from '../lib/trpc/trpc';

type FeedbackState = 'none' | 'like' | 'dislike' | 'submitted';

interface UseAIMessageFeedbackReturn {
  feedbackState: FeedbackState;
  handleThumbsUp: () => void;
  handleThumbsDown: () => void;
  handleSubmitFeedback: (feedback: string) => void;
}

interface HistoryPage {
  messages: ChatMessage[];
  nextCursor: string | undefined;
}

interface HistoryQueryData {
  pages: HistoryPage[];
  pageParams: (string | undefined)[];
}

export const useAIMessageFeedback = (
  messageId: string,
  isStreaming: boolean,
): UseAIMessageFeedbackReturn => {
  const [feedbackState, setFeedbackState] = useState<FeedbackState>('none');
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();

  // Flip hasFeedback=true on the matching message in any cached history
  // page. This makes the persisted feedback survive close+reopen of the
  // chat window without a refetch.
  const markFeedbackPersisted = useCallback(() => {
    queryClient.setQueriesData<HistoryQueryData>(
      { queryKey: ['tinyai-history'] },
      old => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            messages: page.messages.map(m =>
              m.id === messageId ? { ...m, hasFeedback: true } : m,
            ),
          })),
        };
      },
    );
  }, [messageId, queryClient]);

  const handleThumbsUp = useCallback(async () => {
    if (isStreaming) return;

    setFeedbackState('like');

    try {
      const result = await trpc.createChatMessageFeedback.mutate({
        chatMessageId: messageId,
        value: 'like',
        message: null,
      });
      if (!result.success) {
        setFeedbackState('none');
        return;
      }
      markFeedbackPersisted();
      trackEvent('TinyAI Thumbs Up', {
        message_id: messageId,
      });
    } catch (error) {
      console.error(
        '[useAIMessageFeedback] Failed to submit thumbs up feedback:',
        error,
      );
      setFeedbackState('none');
    }
  }, [messageId, isStreaming, trackEvent, markFeedbackPersisted]);

  const handleThumbsDown = useCallback(() => {
    if (isStreaming) return;

    setFeedbackState('dislike');
  }, [isStreaming]);

  const handleSubmitFeedback = useCallback(
    async (feedback: string) => {
      setFeedbackState('submitted');

      try {
        const result = await trpc.createChatMessageFeedback.mutate({
          chatMessageId: messageId,
          value: 'dislike',
          message: feedback || null,
        });
        if (!result.success) {
          setFeedbackState('dislike');
          return;
        }
        markFeedbackPersisted();
        trackEvent('TinyAI Thumbs Down', {
          message_id: messageId,
          feedback_message: feedback || undefined,
        });
      } catch (error) {
        console.error(
          '[useAIMessageFeedback] Failed to submit dislike feedback:',
          error,
        );
        setFeedbackState('dislike');
      }
    },
    [messageId, trackEvent, markFeedbackPersisted],
  );

  return {
    feedbackState,
    handleThumbsUp,
    handleThumbsDown,
    handleSubmitFeedback,
  };
};
